import { CompetitiveChallengeType } from "../enum/v2/competitive-challenge-type";
import { ProgressValueType } from "../enum/v2/progress-value-type";

export function formatNumberOrZero(value: unknown): string | number {
  if (typeof value === "number") {
    if (value % 1 === 0) {
      return value;
    } else {
      return value.toFixed(2);
    }
  }
  return "0";
}

type LocalizedNumberFormatOptions = {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  roundUp?: boolean;
};

function getFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsedValue = Number(value);

    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return null;
}

export function formatLocalizedNumberOrZero(
  value: unknown,
  options: LocalizedNumberFormatOptions = {}
): string {
  const numericValue = getFiniteNumber(value);

  if (numericValue === null) {
    return "0";
  }

  const maximumFractionDigits = options.maximumFractionDigits ?? 2;
  const minimumFractionDigits = options.minimumFractionDigits ?? 0;
  const formattedValue =
    options.roundUp && maximumFractionDigits === 0
      ? Math.ceil(numericValue)
      : numericValue;

  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(formattedValue);
}

export function formatCompetitiveChallengeProgress(
  value: unknown,
  challengeType?: CompetitiveChallengeType,
  options: Pick<LocalizedNumberFormatOptions, "roundUp"> = {}
): string {
  const progressValueType =
    challengeType === CompetitiveChallengeType.COMPETITIVE_CHALLENGE_TYPE_STEPS
      ? ProgressValueType.Steps
      : challengeType ===
          CompetitiveChallengeType.COMPETITIVE_CHALLENGE_TYPE_BICYCLE
        ? ProgressValueType.Kilometers
        : undefined;

  return formatChallengeProgressValue(value, progressValueType, options);
}

export function formatChallengeProgressValue(
  value: unknown,
  progressValueType?: ProgressValueType,
  options: Pick<LocalizedNumberFormatOptions, "roundUp"> = {}
): string {
  const isSteps = progressValueType === ProgressValueType.Steps;
  const isKilometers = progressValueType === ProgressValueType.Kilometers;

  return formatLocalizedNumberOrZero(value, {
    minimumFractionDigits: isKilometers ? 2 : 0,
    maximumFractionDigits: isSteps ? 0 : 2,
    roundUp: isSteps && options.roundUp,
  });
}

export function formatCompetitiveChallengeCo2(value: unknown): string {
  return formatLocalizedNumberOrZero(value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
